// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  DiskEncryptionSet,
  DiskEncryptionSetsListOptionalParams,
  DiskEncryptionSetsListByResourceGroupOptionalParams,
  DiskEncryptionSetsListAssociatedResourcesOptionalParams,
  DiskEncryptionSetsGetOptionalParams,
  DiskEncryptionSetsGetResponse,
  DiskEncryptionSetsCreateOrUpdateOptionalParams,
  DiskEncryptionSetsCreateOrUpdateResponse,
  DiskEncryptionSetUpdate,
  DiskEncryptionSetsUpdateOptionalParams,
  DiskEncryptionSetsUpdateResponse,
  DiskEncryptionSetsDeleteOptionalParams,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a DiskEncryptionSets. */
export interface DiskEncryptionSets {
  /**
   * Lists all the disk encryption sets under a subscription.
   * @param options The options parameters.
   */
  list(
    options?: DiskEncryptionSetsListOptionalParams,
  ): PagedAsyncIterableIterator<DiskEncryptionSet>;
  /**
   * Lists all the disk encryption sets under a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  listByResourceGroup(
    resourceGroupName: string,
    options?: DiskEncryptionSetsListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<DiskEncryptionSet>;
  /**
   * Lists all resources that are encrypted with this disk encryption set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param options The options parameters.
   */
  listAssociatedResources(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    options?: DiskEncryptionSetsListAssociatedResourcesOptionalParams,
  ): PagedAsyncIterableIterator<string>;
  /**
   * Gets information about a disk encryption set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    options?: DiskEncryptionSetsGetOptionalParams,
  ): Promise<DiskEncryptionSetsGetResponse>;
  /**
   * Creates or updates a disk encryption set
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param diskEncryptionSet disk encryption set object supplied in the body of the Put disk encryption
   *                          set operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    diskEncryptionSet: DiskEncryptionSet,
    options?: DiskEncryptionSetsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DiskEncryptionSetsCreateOrUpdateResponse>,
      DiskEncryptionSetsCreateOrUpdateResponse
    >
  >;
  /**
   * Creates or updates a disk encryption set
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param diskEncryptionSet disk encryption set object supplied in the body of the Put disk encryption
   *                          set operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    diskEncryptionSet: DiskEncryptionSet,
    options?: DiskEncryptionSetsCreateOrUpdateOptionalParams,
  ): Promise<DiskEncryptionSetsCreateOrUpdateResponse>;
  /**
   * Updates (patches) a disk encryption set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param diskEncryptionSet disk encryption set object supplied in the body of the Patch disk
   *                          encryption set operation.
   * @param options The options parameters.
   */
  beginUpdate(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    diskEncryptionSet: DiskEncryptionSetUpdate,
    options?: DiskEncryptionSetsUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DiskEncryptionSetsUpdateResponse>,
      DiskEncryptionSetsUpdateResponse
    >
  >;
  /**
   * Updates (patches) a disk encryption set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param diskEncryptionSet disk encryption set object supplied in the body of the Patch disk
   *                          encryption set operation.
   * @param options The options parameters.
   */
  beginUpdateAndWait(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    diskEncryptionSet: DiskEncryptionSetUpdate,
    options?: DiskEncryptionSetsUpdateOptionalParams,
  ): Promise<DiskEncryptionSetsUpdateResponse>;
  /**
   * Deletes a disk encryption set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    options?: DiskEncryptionSetsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes a disk encryption set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param diskEncryptionSetName The name of the disk encryption set that is being created. The name
   *                              can't be changed after the disk encryption set is created. Supported characters for the name are
   *                              a-z, A-Z, 0-9, _ and -. The maximum name length is 80 characters.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    diskEncryptionSetName: string,
    options?: DiskEncryptionSetsDeleteOptionalParams,
  ): Promise<void>;
}
